import {Section, SectionItem} from 'common/components/items-menu/types';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {EntityType} from 'common/report/util/saved-entities/types';
import {SearchDisplayContext} from 'common/report/util/search/types';

export const UntitledReportName = `Untitled`;
export const REPORT_HEADER_HEIGHT = 60;

export enum HeaderAction {
  // TODO(@gonzalo): This action is dashboard specific, when boards mode is enabled. Should be use a `slot` the right call instead?
  ToggleGlobalFilters = `toggle-global-filters`,
  Delete = `delete`,
  Edit = `edit`,
  Refresh = `refresh`,
  Save = `save`,
  SaveNew = `save-new`,
  Duplicate = `duplicate`,
  // TODO: rename
  Load = `open`,
  Reset = `new`,

  CreatedBy = `created-by`,
  Share = `share`,

  // alerts
  CreateAlert = `create-alert`,
  ManageAlert = `manage-alert`,
  EnableAlerts = `enable-alerts`,

  QuerySampling = `query-sampling`,
  Undo = `undo`,
  Redo = `redo`,
}

export type ResultsKeysAndValues = {
  name: string;
  displayName: string;
  values: string[];
  displayValues: string[];
  isPercentage?: boolean;
};

export type HeaderCustomAlertAttrs =
  | {
      'is-custom-alerts-action-visible': true;
      'enable-custom-alert-condition-types'?: boolean;
      'results-keys-and-values': ResultsKeysAndValues[];
      'custom-alert-time-unit'?: string;
      'custom-alerts-disabled-text'?: string;
      'custom-alert-interval-days'?: number;
    }
  | EmptyObj;

export type HeaderUserInfo = {
  id: number;
  name: string;
  email: string;
  date?: string;
};

export type QueryReportHeaderAttrs = {
  'auto-refresh-interval-ms': number | null;
  'computed-at': string;
  'control-items': Array<SectionItem | {slot: string}>;
  'display-context': SearchDisplayContext;
  'duplicate-report-url'?: string;
  'empty-entity-name': string;
  'entities-loading': boolean;
  'entity-type': EntityType.Report | EntityType.Dashboard;
  'has-unsaved-changes': boolean;
  'hide-actions': HeaderAction[];
  'invalid-entity': boolean;
  'invalid-message': string;
  'is-public-dashboard'?: boolean;
  'is-template-dashboard'?: boolean;
  'is-save-failed': boolean;
  'toggle-global-filters-label': string;
  'overflow-control-items': Section[];
  'origin-dashboard'?: DashboardOrigin;
  'project-as-of-date': string;
  'project-id': number;
  'selected-bookmark-dashboard': DashboardOrigin;
  'selected-entity-description': string;
  'selected-entity-id': number | null;
  'selected-entity-name': string;
  'selected-entity-creator'?: HeaderUserInfo;
  'selected-entity-editor'?: HeaderUserInfo;
  'track-name': string;
  'user-id': number;
  'workspace-id': number;

  // permissions
  'can-write': boolean;
  'can-save': boolean;
  'can-share': boolean;
  'is-superadmin': boolean;
  'allow-staff-override': boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': string;

  // query sampling
  'sampling-factor': number;
  'is-query-sampling-enabled': boolean;
  'is-query-sampling-action-visible': boolean;
  'query-sampling-disabled-message': string;
} & HeaderCustomAlertAttrs;

export const queryReportHeaderAttrsSchema = {
  'allow-staff-override': `boolean`,
  'auto-refresh-interval-ms': {
    default: null,
    description: `If set, creates an interval that fires a refresh event every x milliseconds.`,
    type: `number`,
  },
  'can-save': {
    default: false,
    description: `Can the user change the currently selected entity?`,
    type: `boolean`,
  },
  'can-share': {
    default: false,
    description: `Can the user share the currently selected entity?`,
    type: `boolean`,
  },
  // permissions
  'can-write': {
    default: false,
    description: `Can the user create/change entities?`,
    type: `boolean`,
  },
  'computed-at': `string`,
  'control-items': {
    default: [],
    description: `Additional set of items to be rendered in the control bar, interface ControlItem[]`,
    type: `json`,
  },
  'custom-alert-time-unit': `string`,
  'custom-alerts-disabled-text': `string`,
  'display-context': {
    description: `display context for the load entity modal: insights, flows, dashboards, etc (SearchDisplayContext)`,
    enum: Object.values(SearchDisplayContext),
    type: `string`,
  },
  'duplicate-report-url': {
    description: `URL containing serialized report, used to create duplicate in a new tab.`,
    type: `string`,
  },
  'empty-entity-name': `string`,
  'enable-custom-alert-condition-types': `boolean`,
  'entities-loading': `boolean`,
  'entity-type': {
    default: EntityType.Report,
    enum: Object.values(EntityType),
    type: `string`,
  },
  'has-unsaved-changes': `boolean`,
  'hide-actions': {
    default: [],
    description: `Hide a set of built-in header controls.`,
    tsType: `HeaderAction[]`,
    type: `json`,
  },
  'invalid-entity': {
    default: false,
    description: `If entity is invalid, save will be prevented - create new or edit`,
    type: `boolean`,
  },
  'invalid-message': {
    description: `Optional tooltip to display above disabled "Save" button when entity is invalid`,
    type: `string`,
  },
  // custom-alerts
  'custom-alert-interval-days': `number`,
  'is-custom-alerts-action-visible': `boolean`,
  'is-public-dashboard': `boolean`,
  'is-query-sampling-action-visible': `boolean`,
  'is-query-sampling-enabled': `boolean`,
  'is-save-failed': {
    default: false,
    description: `Save report failed`,
    type: `boolean`,
  },
  'is-superadmin': `boolean`,
  'is-template-dashboard': `boolean`,
  'origin-dashboard': {
    description: `Board where the user comes from`,
    tsType: `DashboardOrigin`,
    type: `json`,
  },
  'overflow-control-items': {
    default: [],
    description: `Items to be rendered in the overflow section of the control bar, interface ControlItem[]`,
    type: `json`,
  },
  'project-as-of-date': {
    description: `as of date for projects with data in the past.`,
    type: `string`,
  },
  'project-id': `number`,
  'query-sampling-disabled-message': `string`,
  'results-keys-and-values': `json`,
  // query sampling
  'sampling-factor': `number`,
  'selected-bookmark-dashboard': {
    description: `Board's main dashboard`,
    tsType: `DashboardOrigin`,
    type: `json`,
  },
  'selected-entity-creator': {
    tsType: `HeaderUserInfo`,
    type: `json`,
  },
  'selected-entity-description': `string`,
  'selected-entity-editor': {
    tsType: `HeaderUserInfo`,
    type: `json`,
  },
  'selected-entity-id': `number`,
  'selected-entity-name': `string`,
  'toggle-global-filters-label': `string`,
  'track-name': {
    description: `If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with tab=<track-name> as a property`,
    type: `string`,
  },
  'user-id': `number`,
  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': {
    description: `Message to display above visibility controls`,
    type: `string`,
  },
  'workspace-id': `number`,
};

export enum SaveModes {
  SaveNew = `save-new`,
  Edit = `edit`,
  Save = `save`,
}
